import { useId, type ReactNode } from "react";
import type { ClubCrest, CrestPiece, CrestShapeData } from "@legacy-manager/engine";

/**
 * O escudo desenhado pelo jogador.
 *
 * Mesmo `viewBox` e mesma tipografia do `Crest` dos átomos e do escudo
 * cosmético, de propósito: uma tabela com um clube criado e dezenove comuns
 * precisa continuar alinhada, e o escudo criado troca de desenho, nunca de
 * tamanho nem de peso na linha.
 *
 * **Nada aqui depende do catálogo.** O desenho de cada peça vem dentro do
 * próprio escudo, e não como referência a uma figura. O catálogo tem 651 KB
 * comprimidos e o escudo aparece em toda lista de vinte linhas: buscá-lo para
 * desenhar uma tabela seria absurdo, e no online seria pedir que quem vê o
 * escudo de outra pessoa tenha o mesmo catálogo.
 *
 * **Nada aqui escreve marcação.** As formas chegam como dado e viram elementos
 * de verdade. É o que faz o escudo de outra pessoa, vindo pela rede, não ser um
 * caminho para injetar script.
 */

/** Contorno de cada forma, no mesmo `viewBox` do escudo padrão. */
const CONTORNO: Record<ClubCrest["shape"], string> = {
  shield: "M20 1 38 6v20c0 8-8 14-18 17C10 40 2 34 2 26V6Z",
  circle: "M20 3a19 19 0 1 1 0 38 19 19 0 1 1 0-38Z",
  diamond: "M20 1 38 22 20 43 2 22Z",
  pennant: "M7 2h26v38l-13-9-13 9Z",
};

/** A grade do escudo, que é a do `viewBox` de sempre. */
const LARGURA = 40;
const ALTURA = 44;

/**
 * A partir de quantos pixels o desenho fala por si e a sigla sai da frente.
 *
 * A sigla existe para identificar a linha quando o escudo tem 20 pixels e o
 * desenho é uma mancha colorida. Nesse tamanho ela é a única coisa legível, e
 * tirá-la deixaria vinte manchas iguais numa tabela.
 *
 * Grande é o contrário: três letras pesadas em cima de um leão que a pessoa
 * escolheu e posicionou apagam justamente o que ela fez. Medido na amostra, em
 * 160 pixels a coroa desaparecia por trás do "VIT".
 *
 * O corte fica acima do cabeçalho do clube, que usa 54: lá a sigla continua,
 * como em todo escudo do jogo. Quem passa daqui é a prévia do criador e o
 * cartão de compartilhar, onde o desenho é o assunto.
 */
const SIGLA_ATE = 64;

/**
 * Uma forma solta, desenhada na grade de origem dela.
 *
 * A cor e o traço vêm de fora, e não do dado: o que o catálogo guardou é a
 * geometria, e quem pinta é quem desenha. Assim a mesma figura serve de peça
 * preta num escudo claro e de peça branca num escuro.
 */
function Forma({ forma }: { forma: CrestShapeData }): ReactNode {
  if (forma.t === "p") return <path d={forma.d} />;
  if (forma.t === "c") return <circle cx={forma.cx} cy={forma.cy} r={forma.r} />;
  if (forma.t === "e") return <ellipse cx={forma.cx} cy={forma.cy} rx={forma.rx} ry={forma.ry} />;
  return (
    <rect
      x={forma.x}
      y={forma.y}
      width={forma.w}
      height={forma.h}
      rx={forma.rx}
      ry={forma.ry}
    />
  );
}

/**
 * Uma peça posicionada dentro do escudo.
 *
 * As medidas da peça vão de 0 a 100, e a grade do desenho é 512 nas figuras
 * cheias e 24 nos contornos. A conta que junta as duas coisas está aqui, num
 * lugar só.
 *
 * O tamanho é medido contra a **largura** do escudo nos dois eixos, e não
 * contra largura e altura. Medir contra os dois esticaria toda figura em 10%
 * na vertical, porque a grade do escudo é 40 por 44.
 *
 * Contorno ganha espessura proporcional ao tamanho da peça. Sem isso a linha
 * fica com a espessura da grade de origem, que numa peça pequena some e numa
 * grande vira borrão.
 */
function Peca({ peca }: { peca: CrestPiece }): ReactNode {
  const lado = (peca.size / 100) * LARGURA;
  const escala = lado / peca.grid;
  const centroX = (peca.x / 100) * LARGURA;
  const centroY = (peca.y / 100) * ALTURA;
  const meio = peca.grid / 2;

  return (
    <g
      transform={`translate(${centroX} ${centroY}) rotate(${peca.rotation}) scale(${escala}) translate(${-meio} ${-meio})`}
      fill={peca.outline ? "none" : peca.color}
      stroke={peca.outline ? peca.color : "none"}
      strokeWidth={peca.outline ? 2 : undefined}
      strokeLinecap={peca.outline ? "round" : undefined}
      strokeLinejoin={peca.outline ? "round" : undefined}
    >
      {peca.draw.map((forma, indice) => (
        <Forma key={indice} forma={forma} />
      ))}
    </g>
  );
}

/** O que vai atrás das peças: o fundo e o padrão dele. */
function Fundo({ crest }: { crest: ClubCrest }): ReactNode {
  const { pattern, primary, secondary } = crest;

  if (pattern === "halves") {
    return (
      <>
        <rect x="0" y="0" width="40" height="44" fill={primary} />
        <rect x="20" y="0" width="20" height="44" fill={secondary} />
      </>
    );
  }

  if (pattern === "sash") {
    return (
      <>
        <rect x="0" y="0" width="40" height="44" fill={primary} />
        <path d="M-6 30 24-6h12L6 44H-6Z" fill={secondary} />
      </>
    );
  }

  if (pattern === "stripes") {
    return (
      <>
        <rect x="0" y="0" width="40" height="44" fill={primary} />
        {[4, 14, 24, 34].map((x) => (
          <rect key={x} x={x} y="0" width="5" height="44" fill={secondary} />
        ))}
      </>
    );
  }

  return <rect x="0" y="0" width="40" height="44" fill={primary} />;
}

export function EscudoDoClube({
  crest,
  short,
  size = 40,
  semSigla = false,
}: {
  crest: ClubCrest;
  short: string;
  size?: number;
  /** Tira a sigla de dentro do escudo, para quem a escreve por fora. */
  semSigla?: boolean;
}): ReactNode {
  // Dois escudos na mesma tela não podem dividir o mesmo recorte, senão o
  // segundo herda a forma do primeiro.
  const recorte = `escudo-criado-${useId()}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${LARGURA} ${ALTURA}`}
      aria-hidden="true"
      style={{ flexShrink: 0 }}
    >
      <defs>
        <clipPath id={recorte}>
          <path d={CONTORNO[crest.shape]} />
        </clipPath>
      </defs>

      <g clipPath={`url(#${recorte})`}>
        <Fundo crest={crest} />
        {crest.pieces.map((peca, indice) => (
          <Peca key={indice} peca={peca} />
        ))}
      </g>

      <path
        d={CONTORNO[crest.shape]}
        fill="none"
        stroke={crest.detail}
        strokeWidth="2"
        strokeLinejoin="round"
      />

      {/*
        A sigla vai por cima de tudo, e com contorno escuro.

        Sem o contorno, sigla branca sobre peça branca some. Ela só aparece nos
        tamanhos pequenos, onde é o que identifica a linha; grande, ela tapava
        o desenho que a pessoa fez.
      */}
      {size < SIGLA_ATE && !semSigla && (
        <text
          x="20"
          y={crest.shape === "pennant" ? 24 : 26}
          textAnchor="middle"
          fontSize="13"
          fontWeight="800"
          fill="#fff"
          style={{ paintOrder: "stroke", stroke: "rgba(0,0,0,0.45)", strokeWidth: 3 }}
        >
          {short}
        </text>
      )}
    </svg>
  );
}
